import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Activity, Weight, Ruler, CalendarDays, ArrowRight, TrendingUp,
} from 'lucide-react';
import api from '@/lib/api';
import { formatDateFromIso } from '@/lib/datetime';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

export default function CustomerSeguimiento() {
    const [measurements, setMeasurements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await api.get('/customer/measurements');
                setMeasurements(res.data.data || []);
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar las mediciones.');
            } finally { setLoading(false); }
        };
        fetch();
    }, []);

    const chartData = useMemo(() => {
        return [...measurements]
            .filter(m => m.weight)
            .sort((a, b) => new Date(a.measured_at) - new Date(b.measured_at))
            .map(m => ({
                fecha: formatDateFromIso(m.measured_at),
                peso: parseFloat(m.weight),
                bmi: m.bmi ? parseFloat(m.bmi) : null,
            }));
    }, [measurements]);

    const latest = measurements[0] || null;

    if (loading) return <p className="text-white/60 text-center py-8">Cargando...</p>;

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Seguimiento</h1>
            </div>

            {error && (
                <div className="bg-white/5 text-white text-sm p-3 rounded-md mb-4">{error}</div>
            )}

            {/* Latest summary cards */}
            {latest && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center">
                                    <Weight className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-white/60 uppercase tracking-wider">Peso</p>
                                    <p className="text-lg font-bold text-white">{latest.weight} kg</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center">
                                    <Activity className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-white/60 uppercase tracking-wider">IMC</p>
                                    <p className="text-lg font-bold text-white">{latest.bmi ?? '-'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center">
                                    <TrendingUp className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-white/60 uppercase tracking-wider">Grasa corporal</p>
                                    <p className="text-lg font-bold text-white">{latest.body_fat_percentage ? `${latest.body_fat_percentage}%` : '-'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center">
                                    <Ruler className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-white/60 uppercase tracking-wider">Altura</p>
                                    <p className="text-lg font-bold text-white">{latest.height ? `${latest.height} cm` : '-'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Weight chart */}
            {chartData.length > 0 && (
                <Card className="bg-[#0a0a0a] border-hairline-dark mb-6">
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                            <TrendingUp className="h-4 w-4" />
                        </div>
                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">Evolución del Peso</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3f" />
                                    <XAxis dataKey="fecha" stroke="#5a5a5f" fontSize={12} />
                                    <YAxis stroke="#5a5a5f" fontSize={12} domain={['auto', 'auto']} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #3a3a3f', color: '#fff' }}
                                        itemStyle={{ color: '#fff' }}
                                    />
                                    <Line type="monotone" dataKey="peso" stroke="#fff" strokeWidth={2} dot={{ r: 3, fill: '#fff' }} activeDot={{ r: 5 }} name="Peso (kg)" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Measurements list */}
            <h2 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-3">Historial de mediciones</h2>
            {measurements.length === 0 ? (
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="py-12 text-center">
                        <Activity className="h-12 w-12 text-white/60 mx-auto mb-4" />
                        <p className="text-white/60">No tenés mediciones registradas.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {measurements.map(m => (
                        <Card key={m.id} className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                        <CalendarDays className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                                            {formatDateFromIso(m.measured_at)}
                                        </CardTitle>
                                        <p className="text-xs text-white/60">{m.measured_by || 'Sin profesional'}</p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-sm">
                                    {m.weight && (
                                        <div className="flex items-center gap-2">
                                            <Weight className="h-3 w-3 text-white/60" />
                                            <span className="text-white/60">Peso:</span>
                                            <span className="font-medium">{m.weight} kg</span>
                                        </div>
                                    )}
                                    {m.height && (
                                        <div className="flex items-center gap-2">
                                            <Ruler className="h-3 w-3 text-white/60" />
                                            <span className="text-white/60">Altura:</span>
                                            <span className="font-medium">{m.height} cm</span>
                                        </div>
                                    )}
                                    {m.bmi && (
                                        <div className="flex items-center gap-2">
                                            <Activity className="h-3 w-3 text-white/60" />
                                            <span className="text-white/60">IMC:</span>
                                            <span className="font-medium">{m.bmi}</span>
                                        </div>
                                    )}
                                    {m.body_fat_percentage && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-white/60">Grasa:</span>
                                            <span className="font-medium">{m.body_fat_percentage}%</span>
                                        </div>
                                    )}
                                    {m.muscle_mass_kg && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-white/60">Músculo:</span>
                                            <span className="font-medium">{m.muscle_mass_kg} kg</span>
                                        </div>
                                    )}
                                    {m.waist && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-white/60">Cintura:</span>
                                            <span className="font-medium">{m.waist} cm</span>
                                        </div>
                                    )}
                                    {m.bone_mass && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-white/60">Masa ósea:</span>
                                            <span className="font-medium">{m.bone_mass} kg</span>
                                        </div>
                                    )}
                                    {m.body_water_percentage && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-white/60">Agua:</span>
                                            <span className="font-medium">{m.body_water_percentage}%</span>
                                        </div>
                                    )}
                                    {m.visceral_fat && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-white/60">Grasa visceral:</span>
                                            <span className="font-medium">{m.visceral_fat}</span>
                                        </div>
                                    )}
                                    {m.basal_metabolism && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-white/60">Metabolismo:</span>
                                            <span className="font-medium">{m.basal_metabolism} kcal</span>
                                        </div>
                                    )}
                                </div>
                                {/* Circumferences */}
                                {(m.chest || m.waist || m.abdomen || m.hips || m.neck || m.right_arm || m.left_arm) && (
                                    <div className="mt-3 pt-3 border-t border-[#3a3a3f]">
                                        <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Perímetros (cm)</p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
                                            {m.chest && <span className="text-white/60">Pecho: <span className="text-white font-medium">{m.chest}</span></span>}
                                            {m.abdomen && <span className="text-white/60">Abdomen: <span className="text-white font-medium">{m.abdomen}</span></span>}
                                            {m.hips && <span className="text-white/60">Cadera: <span className="text-white font-medium">{m.hips}</span></span>}
                                            {m.neck && <span className="text-white/60">Cuello: <span className="text-white font-medium">{m.neck}</span></span>}
                                            {m.right_arm && <span className="text-white/60">Brazo der.: <span className="text-white font-medium">{m.right_arm}</span></span>}
                                            {m.left_arm && <span className="text-white/60">Brazo izq.: <span className="text-white font-medium">{m.left_arm}</span></span>}
                                            {m.right_forearm && <span className="text-white/60">Antebrazo der.: <span className="text-white font-medium">{m.right_forearm}</span></span>}
                                            {m.left_forearm && <span className="text-white/60">Antebrazo izq.: <span className="text-white font-medium">{m.left_forearm}</span></span>}
                                            {m.right_thigh && <span className="text-white/60">Muslo der.: <span className="text-white font-medium">{m.right_thigh}</span></span>}
                                            {m.left_thigh && <span className="text-white/60">Muslo izq.: <span className="text-white font-medium">{m.left_thigh}</span></span>}
                                            {m.right_calf && <span className="text-white/60">Pantorrilla der.: <span className="text-white font-medium">{m.right_calf}</span></span>}
                                            {m.left_calf && <span className="text-white/60">Pantorrilla izq.: <span className="text-white font-medium">{m.left_calf}</span></span>}
                                        </div>
                                    </div>
                                )}
                                {m.observations && <p className="text-xs text-white/50 mt-2">{m.observations}</p>}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
